import { HttpTransportType, HubConnection, LogLevel } from '@microsoft/signalr';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { useOktaAuth } from '@okta/okta-react';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AquaSpecies } from 'types/shared/aqua-species.type';
import { loadState, saveState } from 'utils/storage';
import { usePondActions } from 'views/production-systems/hooks/use-pond-actions';
import { LockedPonds } from 'views/production-systems/types/locked-ponds/locked-ponds.type';
import { ConnectionType } from 'views/production-systems/types/locked-ponds/signal-r/connection-type';
import { LockedPondsChangeMessage } from 'views/production-systems/types/locked-ponds/signal-r/locked-ponds-change-message.type';
import { SiteChangeMessage } from 'views/production-systems/types/locked-ponds/signal-r/sites-change-messag.type';
import { RegistrationDateType } from 'views/production-systems/types/pond.type';

type State = {
  lockedPonds: LockedPonds;
  globalSiteId: string;
  registrationDate: Dayjs;
  changeRegistrationDate: (date: Dayjs, key: RegistrationDateType) => void;
  registrationLogDate: Dayjs;
  registrationSetupDate: Dayjs;
  changeGlobalSiteId: (id: string) => void;
  disconnectSignalR: () => Promise<void>;
  lockPonds: (pondIds: string[]) => void;
  releasePonds: (pondIds: string[]) => void;
  extendPonds: (pondIds: string[]) => void;
  selectedPonds: string[];
  setSelectedPonds: (pondIds: string[]) => void;
  error: AxiosError<unknown, any> | null;
};

type PondsProviderProps = {
  children: React.ReactNode;
};

const PondsContext = React.createContext<State | undefined>(undefined);

const GetInitialLockedPonds = () => {
  return {
    granted: [],
    notGranted: [],
  };
};

const apiRoot = `${import.meta.env.VITE_REST_API_URL}`;

const PondsProvider: React.FC<PondsProviderProps> = ({ children }) => {
  const today = dayjs();
  const { oktaAuth, authState } = useOktaAuth();
  const queryClient = useQueryClient();
  const connectionRef = useRef<HubConnection | undefined>(undefined);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const shouldReconnectRef = useRef(false);
  const [selectedPonds, setSelectedPonds] = useState<string[]>([]);
  const [lockedPonds, setLockedPonds] = useState<LockedPonds>(GetInitialLockedPonds());
  const [globalSiteId, setGlobalSiteId] = useState<string>(loadState({ key: 'globalSiteId', storage: 'session' }) || '');

  const [registrationDate, setRegistrationDate] = useState<Dayjs>(
    dayjs(
      loadState({
        key: RegistrationDateType.overview,
        storage: 'session',
      }) || today
    )
  );


  const [registrationLogDate, setRegistrationLogDate] = useState<Dayjs>(
    dayjs(
      loadState({
        key: RegistrationDateType.log,
        storage: 'session',
      }) || today
    )
  );


  const [registrationSetupDate, setRegistrationSetupDate] = useState<Dayjs>(
    dayjs(
      loadState({
        key: RegistrationDateType.setup,
        storage: 'session',
      }) || today
    )
  );

  const {
    lockPonds,
    releasePonds,
    extendPonds,
    lockError: pondActionsError,
  } = usePondActions({
    globalSiteId,
    setLockedPonds,
  });

  const disconnectSignalR = useCallback(async () => {
    const currentConnection = connectionRef.current;
    connectionRef.current = undefined;
    shouldReconnectRef.current = false;

    if (reconnectTimeoutRef.current !== undefined) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (!currentConnection) return;

    try {
      await currentConnection.stop();
    } catch (error) {
      console.error('Could not close SignalR connection.', error);
    }
  }, []);

  // #region FETCH DATA
  const clearQueries = useCallback(
    (date: string, apiCall: 'getPonds' | 'getRegistrations') => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] === apiCall && query.queryKey[1] === globalSiteId) {
            let queryDate = dayjs(String(query.queryKey[2]));
            let dayjsRegDate = dayjs(date);
            let afterDate = dayjsRegDate.isSameOrAfter(queryDate);
            return afterDate;
          }
          return false;
        },
      });
    },
    [queryClient, globalSiteId]
  );
  // #endregion FETCH DATA

  // #region HANDLERS

  const changeRegistrationDate = useCallback((date: Dayjs, key: RegistrationDateType) => {
    saveState({ key: key, storage: 'session', state: date.format('YYYY-MM-DD') });
    console.log('registrationDate: ', date.format('YYYY-MM-DD'));

    if (key === RegistrationDateType.overview) setRegistrationDate(date);
    if (key === RegistrationDateType.log) setRegistrationLogDate(date);
    if (key === RegistrationDateType.setup) setRegistrationSetupDate(date);
  }, []);

  const changeGlobalSiteId = useCallback(
    (id: string): void => {
      if (globalSiteId !== id) void disconnectSignalR();

      if (!!lockedPonds.granted.length) {
        releasePonds(lockedPonds.granted);
      }

      setLockedPonds(GetInitialLockedPonds());

      saveState({ key: 'globalSiteId', storage: 'session', state: id });
      setGlobalSiteId(id);
    },
    [disconnectSignalR, globalSiteId, lockedPonds.granted, releasePonds]
  );

  const handleMessages = useCallback(
    (currentConnection: HubConnection) => {
      currentConnection.on(ConnectionType.LockPondsStatusChange, (message: LockedPondsChangeMessage) => {
        queryClient.invalidateQueries({
          queryKey: ['getPonds', globalSiteId],
        });
      });

      currentConnection.on(ConnectionType.SiteChange, (message: SiteChangeMessage) => {
        if (globalSiteId === message.globalSiteId) {
          clearQueries(message.regDate, 'getPonds');
          clearQueries(message.regDate, 'getRegistrations');
        } else {
          console.error('Site Id does not match currently subscribed Site Id');
        }
      });
    },
    [queryClient, globalSiteId, clearQueries]
  );

  const handleClose = useCallback(
    (currentConnection: HubConnection) => {
      currentConnection.onclose((error) => {
        if (shouldReconnectRef.current && globalSiteId && connectionRef.current === currentConnection) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = undefined;
            if (!shouldReconnectRef.current || connectionRef.current !== currentConnection) return;

            console.log('Attempting to restart connection...');
            currentConnection.start().catch(() => console.log('Something went wrong reconnecting.'));
          }, 5000);
        }

        if (connectionRef.current === currentConnection) {
          queryClient.invalidateQueries({
            queryKey: ['getPonds'],
          });
        }
      });
    },
    [globalSiteId, queryClient]
  );

  // #endregion HANDLERS

  useEffect(() => {
    const type = localStorage.getItem('themeType');

    if (!authState?.isAuthenticated || !globalSiteId || type === AquaSpecies.salmon) {
      void disconnectSignalR();
      return;
    }

    shouldReconnectRef.current = true;
    const url = apiRoot + `/ewosapi/signalr/v1/sitenotifications/${globalSiteId}`;

    const newConnection = new HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        // Resolved on every connect and reconnect, so a rotated token is picked up automatically
        accessTokenFactory: () => oktaAuth.getAccessToken() ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connectionRef.current = newConnection;

    newConnection
      .start()
      .then((result) => {
        if (connectionRef.current !== newConnection) return;

        console.log('Connected.');
        handleMessages(newConnection);
        handleClose(newConnection);
      })
      .catch((e) => {
        console.log('Connection failed: ', e);
      });

    return () => {
      if (connectionRef.current === newConnection) void disconnectSignalR();
    };
  }, [authState?.isAuthenticated, oktaAuth, globalSiteId, handleMessages, handleClose, disconnectSignalR]);

  return (
    <PondsContext.Provider
      value={{
        lockPonds,
        releasePonds,
        extendPonds,
        lockedPonds,
        globalSiteId,
        changeGlobalSiteId,
        disconnectSignalR,
        registrationDate,
        changeRegistrationDate,
        registrationLogDate,
        registrationSetupDate,
        error: pondActionsError,
        selectedPonds: selectedPonds,
        setSelectedPonds: setSelectedPonds,
      }}>
      {children}
    </PondsContext.Provider>
  );
};

function usePondsState() {
  const context = useContext(PondsContext);
  if (context === undefined) {
    throw new Error('usePondsState must be used within a LockedPondsContext');
  }
  return context;
}

export { PondsProvider, usePondsState };
